import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../lib/supabase';
import { useAuth } from './AuthContext';

const EventContext = createContext();

export const useEvents = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvents must be used within an EventProvider');
  }
  return context;
};

export const EventProvider = ({ children }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favoriteEvents, setFavoriteEvents] = useState(new Set());
  const [rsvpEvents, setRsvpEvents] = useState(new Map()); // Map of eventId -> status ('attending', 'interested')

  // Initial mock events data until backend is connected
  const mockEvents = [
    {
      id: 1,
      title: 'Republican Town Hall Meeting',
      type: 'townhall',
      date: '2024-05-15',
      time: '18:00',
      endTime: '20:00',
      location: 'Dallas, TX',
      address: 'Convention Center, 650 S Griffin St',
      coordinates: { lat: 32.7767, lng: -96.8089 },
      attendees: 120,
      description: 'Join us for an evening of discussion on local issues affecting our community. Meet your representatives and voice your concerns.',
      image: 'https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?w=400&h=250&fit=crop',
      organizer: 'Dallas Republican Committee',
      price: 'Free',
      createdBy: 1,
      status: 'approved',
      website: 'https://dallasrepublicans.org',
      contactEmail: 'events@dallasrepublicans.org'
    },
    {
      id: 2,
      title: 'Second Amendment Rally',
      type: 'rally',
      date: '2024-05-22',
      time: '10:00',
      endTime: '14:00',
      location: 'Houston, TX',
      address: 'Liberty Square, 123 Freedom Blvd',
      coordinates: { lat: 29.7604, lng: -95.3698 },
      attendees: 350,
      description: 'Stand with fellow patriots to defend our constitutional right to bear arms. Hear from speakers about current legislation and how to protect our freedoms.',
      image: 'https://images.unsplash.com/photo-1553831755-2a0a5a9b033d?w=400&h=250&fit=crop',
      organizer: 'Texas Gun Rights Coalition',
      price: 'Free',
      createdBy: 2,
      status: 'approved',
      website: 'https://txgunrights.org',
      contactEmail: 'rally@txgunrights.org'
    },
    {
      id: 3,
      title: 'Conservative Networking Event',
      type: 'networking',
      date: '2024-05-18',
      time: '19:00',
      endTime: '21:00',
      location: 'Austin, TX',
      address: 'Capitol Grill, 500 Congress Ave',
      coordinates: { lat: 30.2672, lng: -97.7431 },
      attendees: 85,
      description: 'Connect with like-minded professionals in a relaxed setting. Build relationships with conservative business leaders, activists, and community members.',
      image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&h=250&fit=crop',
      organizer: 'Austin Conservative Professionals',
      price: '$15',
      createdBy: 3,
      status: 'approved',
      website: 'https://austinconservatives.org',
      contactEmail: 'network@austinconservatives.org'
    },
    {
      id: 4,
      title: 'Presidential Debate Watch Party',
      type: 'social',
      date: '2024-06-05',
      time: '20:00',
      endTime: '22:30',
      location: 'San Antonio, TX',
      address: 'Liberty Pub, 789 Alamo St',
      coordinates: { lat: 29.4241, lng: -98.4936 },
      attendees: 200,
      description: 'Join fellow Republicans to watch the presidential debate. Food and drinks available for purchase. Discussion to follow.',
      image: 'https://images.unsplash.com/photo-1572025442646-866d16c84a54?w=400&h=250&fit=crop',
      organizer: 'Bexar County Republicans',
      price: 'Free',
      createdBy: 4,
      status: 'approved',
      website: 'https://bexargop.org',
      contactEmail: 'events@bexargop.org'
    },
    {
      id: 5,
      title: 'Conservative Leadership Conference',
      type: 'conference',
      date: '2024-06-15',
      time: '09:00',
      endTime: '17:00',
      location: 'Fort Worth, TX',
      address: 'Patriot Convention Center, 456 Constitution Ave',
      coordinates: { lat: 32.7555, lng: -97.3308 },
      attendees: 500,
      description: 'A full day of inspiring speakers, educational workshops, and networking opportunities for conservative leaders and activists.',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=250&fit=crop',
      organizer: 'Texas Conservative Coalition',
      price: '$50',
      createdBy: 5,
      status: 'approved',
      website: 'https://txconservatives.org',
      contactEmail: 'conference@txconservatives.org'
    },
    {
      id: 6,
      title: 'Young Republicans Mixer',
      type: 'social',
      date: '2024-05-25',
      time: '20:00',
      endTime: '23:00',
      location: 'Dallas, TX',
      address: 'Republic Lounge, 789 Main St',
      coordinates: { lat: 32.7801, lng: -96.8072 },
      attendees: 120,
      description: 'Network with young conservative professionals while enjoying drinks and appetizers. Special guest speakers will discuss opportunities for young people in politics.',
      image: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=400&h=250&fit=crop',
      organizer: 'Dallas Young Republicans',
      price: '$10',
      createdBy: 6,
      status: 'approved',
      website: 'https://dallasyr.org',
      contactEmail: 'social@dallasyr.org'
    }
  ];

  // Load events from Supabase or fallback to mock data
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Try to fetch events from Supabase
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .order('date', { ascending: true });
          
        if (error) throw error;
        
        // If we get data from Supabase, use it; otherwise, use mock data
        if (data && data.length > 0) {
          setEvents(data);
        } else {
          console.log('Using mock events data');
          setEvents(mockEvents);
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to load events. Using sample data instead.');
        // Fallback to mock data
        setEvents(mockEvents);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Load user favorites and RSVPs
  useEffect(() => {
    if (user) {
      const loadUserPreferences = async () => {
        try {
          // Try to fetch user favorites from Supabase
          const { data: favData, error: favError } = await supabase
            .from('user_favorites')
            .select('event_id')
            .eq('user_id', user.id);

          if (!favError && favData) {
            setFavoriteEvents(new Set(favData.map(item => item.event_id)));
          } else {
            // Fallback to mock data
            setFavoriteEvents(new Set([1, 3]));
          }

          // Try to fetch user RSVPs from Supabase
          const { data: rsvpData, error: rsvpError } = await supabase
            .from('user_rsvps')
            .select('event_id, status')
            .eq('user_id', user.id);

          if (!rsvpError && rsvpData) {
            setRsvpEvents(new Map(rsvpData.map(item => [item.event_id, item.status])));
          } else {
            // Fallback to mock data
            setRsvpEvents(new Map([
              [4, 'attending'],
              [6, 'interested']
            ]));
          }
        } catch (err) {
          console.error('Error fetching user preferences:', err);
          // Use mock data as fallback
          setFavoriteEvents(new Set([1, 3]));
          setRsvpEvents(new Map([
            [4, 'attending'],
            [6, 'interested']
          ]));
        }
      };

      loadUserPreferences();
    }
  }, [user]);

  // Toggle favorite status for an event
  const toggleFavorite = async (eventId) => {
    if (!user) return false;
    
    const newFavorites = new Set(favoriteEvents);
    
    try {
      if (newFavorites.has(eventId)) {
        // Remove from favorites
        newFavorites.delete(eventId);
        
        // Try to remove from Supabase
        await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('event_id', eventId);
      } else {
        // Add to favorites
        newFavorites.add(eventId);
        
        // Try to add to Supabase
        await supabase
          .from('user_favorites')
          .insert([{ user_id: user.id, event_id: eventId }]);
      }
      
      setFavoriteEvents(newFavorites);
      return true;
    } catch (err) {
      console.error('Error updating favorites:', err);
      // Still update local state even if database fails
      setFavoriteEvents(newFavorites);
      return true;
    }
  };

  // Update RSVP status for an event
  const updateRSVP = async (eventId, status) => {
    if (!user) return false;
    
    const newRSVPs = new Map(rsvpEvents);
    
    try {
      if (newRSVPs.get(eventId) === status) {
        // User is canceling their RSVP
        newRSVPs.delete(eventId);
        
        // Try to remove from Supabase
        await supabase
          .from('user_rsvps')
          .delete()
          .eq('user_id', user.id)
          .eq('event_id', eventId);
      } else {
        // User is setting or changing their RSVP
        newRSVPs.set(eventId, status);
        
        // Try to upsert to Supabase
        await supabase
          .from('user_rsvps')
          .upsert([{ 
            user_id: user.id, 
            event_id: eventId,
            status: status 
          }], { onConflict: ['user_id', 'event_id'] });
      }
      
      setRsvpEvents(newRSVPs);
      return true;
    } catch (err) {
      console.error('Error updating RSVP:', err);
      // Still update local state even if database fails
      setRsvpEvents(newRSVPs);
      return true;
    }
  };

  // Create a new event
  const createEvent = async (eventData) => {
    if (!user) {
      return { error: 'You must be logged in to create an event' };
    }

    try {
      // Add user ID and default status
      const newEvent = {
        ...eventData,
        createdBy: user.id,
        status: 'pending', // Require moderation before showing publicly
        id: Date.now() // Temporary ID for mock data
      };

      // Try to insert into Supabase
      const { data, error } = await supabase
        .from('events')
        .insert([newEvent])
        .select();

      if (error) throw error;

      if (data) {
        // If successful, update local state with the new event
        setEvents(prevEvents => [...prevEvents, data[0]]);
        return { success: true, data: data[0] };
      } else {
        // Fallback to mock implementation
        setEvents(prevEvents => [...prevEvents, newEvent]);
        return { success: true, data: newEvent };
      }
    } catch (err) {
      console.error('Error creating event:', err);
      // For demo purposes, still add to local state
      const mockNewEvent = {
        ...eventData,
        id: Date.now(),
        createdBy: user.id,
        status: 'pending'
      };
      setEvents(prevEvents => [...prevEvents, mockNewEvent]);
      return { success: true, data: mockNewEvent, warning: 'Added to local state only' };
    }
  };

  // Get event by ID
  const getEventById = (eventId) => {
    return events.find(event => event.id === eventId);
  };

  // Filter events based on criteria
  const filterEvents = (filters = {}) => {
    let filtered = [...events];

    // Filter by type
    if (filters.type && filters.type !== 'all') {
      filtered = filtered.filter(event => event.type === filters.type);
    }

    // Filter by date
    if (filters.date && filters.date !== 'any') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);
      
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);

      switch (filters.date) {
        case 'today':
          filtered = filtered.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate.toDateString() === today.toDateString();
          });
          break;
        case 'tomorrow':
          filtered = filtered.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate.toDateString() === tomorrow.toDateString();
          });
          break;
        case 'weekend':
          filtered = filtered.filter(event => {
            const eventDate = new Date(event.date);
            const day = eventDate.getDay();
            return (day === 0 || day === 6) && eventDate >= today && eventDate < nextWeek;
          });
          break;
        case 'week':
          filtered = filtered.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate >= today && eventDate < nextWeek;
          });
          break;
        case 'month':
          filtered = filtered.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate >= today && eventDate < nextMonth;
          });
          break;
        case 'past':
          filtered = filtered.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate < today;
          });
          break;
        case 'upcoming':
          filtered = filtered.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate >= today;
          });
          break;
      }
    }

    // Filter by price
    if (filters.price && filters.price !== 'any') {
      filtered = filtered.filter(event => {
        if (filters.price === 'free') {
          return event.price === 'Free' || event.price === '$0' || event.price === '';
        } else {
          return event.price !== 'Free' && event.price !== '$0' && event.price !== '';
        }
      });
    }

    // Filter by location
    if (filters.state) {
      filtered = filtered.filter(event => 
        event.location && event.location.toLowerCase().includes(filters.state.toLowerCase())
      );
    }

    if (filters.city) {
      filtered = filtered.filter(event => 
        event.location && event.location.toLowerCase().includes(filters.city.toLowerCase())
      );
    }

    // Filter by organizer
    if (filters.organizer) {
      filtered = filtered.filter(event => 
        event.organizer && event.organizer.toLowerCase().includes(filters.organizer.toLowerCase())
      );
    }

    // Filter by virtual/in-person
    if (filters.virtualOnly) {
      filtered = filtered.filter(event => event.virtualEvent);
    }

    if (filters.inPersonOnly) {
      filtered = filtered.filter(event => !event.virtualEvent);
    }

    // Filter by status (default to approved only for regular users)
    if (filters.status) {
      filtered = filtered.filter(event => event.status === filters.status);
    } else {
      // By default only show approved events
      filtered = filtered.filter(event => event.status === 'approved');
    }

    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(event => (
        (event.title && event.title.toLowerCase().includes(searchTerm)) ||
        (event.description && event.description.toLowerCase().includes(searchTerm)) ||
        (event.location && event.location.toLowerCase().includes(searchTerm)) ||
        (event.organizer && event.organizer.toLowerCase().includes(searchTerm))
      ));
    }

    return filtered;
  };

  const value = {
    events,
    loading,
    error,
    favoriteEvents,
    rsvpEvents,
    toggleFavorite,
    updateRSVP,
    createEvent,
    getEventById,
    filterEvents
  };

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};